<div class="main-content bi">
  <input
    #fileInput
    type="file"
    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    hidden
    [disabled]="loading"
    (change)="onFileSelected($event)"
  />

  <header class="bi__header">
    <nav class="bi__breadcrumb" [attr.aria-label]="'BUSINESS_IMPORT.BREADCRUMB' | translate">
      <a routerLink="..">{{ "BUSINESS_REGISTRY" | translate }}</a>
      <i class="pi pi-chevron-right" aria-hidden="true"></i>
      <span aria-current="page">{{ "BUSINESS_IMPORT.TITLE" | translate }}</span>
    </nav>
    <h1 class="bi__title">{{ "BUSINESS_IMPORT.TITLE" | translate }}</h1>
    <p class="bi__subtitle">
      {{
        (loading
          ? "BUSINESS_IMPORT.SUBTITLE_PROCESSING"
          : step === "preview"
            ? "BUSINESS_IMPORT.SUBTITLE_PREVIEW"
            : step === "upload"
              ? "BUSINESS_IMPORT.SUBTITLE"
              : "BUSINESS_IMPORT.SUBTITLE_COMPLETE"
        ) | translate
      }}
    </p>
  </header>

  <section *ngIf="step === 'upload' && !loading && recentImports.length > 0" class="bi-card bi-recent">
    <h2 class="bi__h3">{{ "BUSINESS_IMPORT.JOB.RECENT" | translate }}</h2>
    <button *ngFor="let job of recentImports" type="button" class="btn btn-default bi-recent__job" (click)="resumeImport(job.JobId ?? '')">
      <span class="bi__wrap">{{ job.UploadedFileName }}</span>
      <span>{{ job.StartedAtUtc | date: 'short' }} · {{ "BUSINESS_IMPORT.JOB." + job.Status?.toUpperCase() | translate }}</span>
    </button>
  </section>

  <!-- Step indicators: status only, never interactive. -->
  <ol class="bi__stepper" [attr.aria-label]="'BUSINESS_IMPORT.STEPPER_LABEL' | translate">
    <li
      *ngFor="let label of ['UPLOAD', 'PREVIEW', 'COMPLETE']; let i = index"
      class="bi__step bi__step--{{ stepStates()[i] }}"
      [attr.aria-current]="stepStates()[i] === 'current' || stepStates()[i] === 'busy' ? 'step' : null"
    >
      <span class="bi__step-marker" aria-hidden="true">
        <ng-container [ngSwitch]="stepStates()[i]">
          <i *ngSwitchCase="'done'" class="pi pi-check"></i>
          <i *ngSwitchCase="'busy'" class="pi pi-spinner pi-spin"></i>
          <i *ngSwitchCase="'failed'" class="pi pi-times"></i>
          <i *ngSwitchCase="'warn'" class="pi pi-exclamation-triangle"></i>
          <span *ngSwitchDefault>{{ i + 1 }}</span>
        </ng-container>
      </span>
      <span class="bi__step-text">
        <span class="bi__step-label">{{ "BUSINESS_IMPORT.STEPS." + label | translate }}</span>
        <span class="bi__step-caption">{{ "BUSINESS_IMPORT.STEP_STATE." + stepStates()[i].toUpperCase() | translate }}</span>
      </span>
    </li>
  </ol>
  <div class="bi__stepper-compact">
    <div class="bi__stepper-compact-text">
      <strong>{{ "BUSINESS_IMPORT.STEP_OF" | translate: { step: activeStepIndex + 1, total: 3 } }} · {{ "BUSINESS_IMPORT.STEPS." + ["UPLOAD", "PREVIEW", "COMPLETE"][activeStepIndex] | translate }}</strong>
      <span>{{ "BUSINESS_IMPORT.STEP_STATE." + stepStates()[activeStepIndex].toUpperCase() | translate }}</span>
    </div>
    <div class="bi__stepper-compact-bar" aria-hidden="true">
      <span *ngFor="let state of stepStates(); let i = index" [class.is-reached]="i <= activeStepIndex"></span>
    </div>
  </div>

  <!-- ================= Processing ================= -->
  <section *ngIf="loading" class="bi__grid" [attr.aria-busy]="true">
    <div class="bi-card bi__main bi-processing">
      <div class="bi-processing__head">
        <span class="bi-processing__icon" aria-hidden="true"><i class="pi pi-spinner pi-spin"></i></span>
        <div role="status" aria-live="polite">
          <h2 #stepHeading tabindex="-1" class="bi__h2">
            {{
              (processing === "validating"
                ? "BUSINESS_IMPORT.PROCESSING.VALIDATING_TITLE"
                : "BUSINESS_IMPORT.PROCESSING.IMPORTING_TITLE"
              ) | translate: { count: rowsSent }
            }}
          </h2>
          <p class="bi__muted">
            {{
              (processing === "validating"
                ? "BUSINESS_IMPORT.PROCESSING.VALIDATING_SUB"
                : "BUSINESS_IMPORT.PROCESSING.NOT_CANCELLABLE"
              ) | translate
            }}
          </p>
        </div>
      </div>
      <div
        class="bi-progress"
        [class.bi-progress--determinate]="jobProgress !== null && processing === 'importing'"
        [attr.aria-valuenow]="jobProgress !== null && processing === 'importing' ? progressPercent : null"
        [attr.aria-valuemin]="jobProgress !== null && processing === 'importing' ? 0 : null"
        [attr.aria-valuemax]="jobProgress !== null && processing === 'importing' ? 100 : null"
        role="progressbar"
        [attr.aria-label]="
          (processing === 'validating'
            ? 'BUSINESS_IMPORT.PROCESSING.VALIDATING_TITLE'
            : 'BUSINESS_IMPORT.PROCESSING.IMPORTING_TITLE'
          ) | translate: { count: rowsSent }
        "
      >
        <span class="bi-progress__bar" [style.width.%]="jobProgress !== null && processing === 'importing' ? progressPercent : null"></span>
      </div>
      <div class="bi-processing__meta">
        <span *ngIf="jobProgress !== null && processing === 'importing'; else unknownProgress" role="status">
          {{ "BUSINESS_IMPORT.JOB.PROGRESS" | translate: { done: processedRows, total: jobProgress.TotalRows, created: jobProgress.CreatedRows, failed: jobProgress.FailedRows } }}
        </span>
        <ng-template #unknownProgress><span>{{ "BUSINESS_IMPORT.PROCESSING.NO_PERCENT" | translate }}</span></ng-template>
        <span>
          <i class="pi pi-clock" aria-hidden="true"></i>
          {{ "BUSINESS_IMPORT.PROCESSING.ELAPSED" | translate }}
          <span class="kuba-mono bi__strong">{{ elapsedLabel }}</span>
        </span>
      </div>
      <ul class="bi-notes">
        <li><i class="pi pi-clock" aria-hidden="true"></i>{{ "BUSINESS_IMPORT.PROCESSING.SEVERAL_MINUTES" | translate }}</li>
        <li><i class="pi pi-info-circle" aria-hidden="true"></i>{{ (jobProgress?.JobId ? "BUSINESS_IMPORT.JOB.BACKGROUND" : "BUSINESS_IMPORT.PROCESSING.KEEP_OPEN") | translate }}</li>
        <li *ngIf="processing === 'importing'">
          <i class="pi pi-info-circle" aria-hidden="true"></i>{{ "BUSINESS_IMPORT.PROCESSING.LOCKED" | translate }}
        </li>
      </ul>
      <p *ngIf="pollError" class="bi-banner bi-banner--warn" role="status">{{ "BUSINESS_IMPORT.JOB.RECONNECTING" | translate }} {{ pollError }}</p>
    </div>
    <aside class="bi-card bi__aside bi-details">
      <h2 class="bi__h3">{{ "BUSINESS_IMPORT.PROCESSING.DETAILS" | translate }}</h2>
      <dl>
        <dt>{{ "BUSINESS_IMPORT.PROCESSING.FILE" | translate }}</dt>
        <dd class="bi__wrap">{{ selectedFile?.name }}</dd>
        <ng-container *ngIf="processing === 'importing'">
          <dt>{{ "BUSINESS_IMPORT.PROCESSING.ROWS_SENT" | translate }}</dt>
          <dd class="kuba-mono">{{ rowsSent }}</dd>
        </ng-container>
        <dt>{{ "BUSINESS_IMPORT.PROCESSING.STARTED" | translate }}</dt>
        <dd>{{ startedAt | date: "HH:mm" }}</dd>
      </dl>
    </aside>
  </section>

  <!-- ================= Upload ================= -->
  <section *ngIf="!loading && step === 'upload'" class="bi-card bi-upload">
    <div class="bi-upload__main">
      <h2 #stepHeading tabindex="-1" class="bi__h2">
        {{ (selectedFile ? "BUSINESS_IMPORT.UPLOAD.HEADING_SELECTED" : "BUSINESS_IMPORT.UPLOAD.HEADING_SELECT") | translate }}
      </h2>

      <div *ngIf="fileError" class="bi-banner bi-banner--error" role="alert">
        <i class="pi pi-times-circle" aria-hidden="true"></i>
        <div>
          <strong>{{ "BUSINESS_IMPORT.UPLOAD.REJECTED_TITLE" | translate }}</strong>
          <span><span *ngIf="rejectedFileName" class="bi__wrap">“{{ rejectedFileName }}”: </span>{{ fileError }}</span>
        </div>
      </div>

      <div *ngIf="apiError" class="bi-banner bi-banner--error" role="alert">
        <i class="pi pi-times-circle" aria-hidden="true"></i>
        <div>
          <strong>{{ "BUSINESS_IMPORT.UPLOAD.VALIDATION_FAILED_TITLE" | translate }}</strong>
          <span>{{ apiError }}</span>
        </div>
      </div>

      <div *ngIf="selectedFile; else dropzone" class="bi-file">
        <div class="bi-file__info">
          <span class="bi-file__icon" aria-hidden="true"><i class="pi pi-file-excel"></i></span>
          <div class="bi-file__text">
            <span class="bi-file__name" [title]="selectedFile.name">{{ selectedFile.name }}</span>
            <span class="bi-file__meta">
              <span>{{ selectedFile.size / 1048576 | number: "1.1-1" }} MB · XLSX</span>
              <span class="bi-file__ready"><i class="pi pi-check-circle" aria-hidden="true"></i>{{ "BUSINESS_IMPORT.UPLOAD.READY" | translate }}</span>
            </span>
          </div>
        </div>
        <div class="bi-file__actions">
          <button type="button" class="btn btn-default" (click)="openFilePicker()">
            <i class="pi pi-refresh" aria-hidden="true"></i>
            <span>{{ "BUSINESS_IMPORT.UPLOAD.REPLACE" | translate }}</span>
          </button>
          <button
            type="button"
            class="kuba-icon-btn bi-file__remove"
            [attr.aria-label]="'BUSINESS_IMPORT.UPLOAD.REMOVE' | translate"
            [title]="'BUSINESS_IMPORT.UPLOAD.REMOVE' | translate"
            (click)="removeSelectedFile()"
          >
            <i class="pi pi-times" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <ng-template #dropzone>
        <div
          class="bi-drop"
          [class.bi-drop--active]="dragActive"
          [class.bi-drop--error]="!!fileError"
          (dragover)="onDragOver($event)"
          (dragleave)="onDragLeave()"
          (drop)="onDrop($event)"
        >
          <span class="bi-drop__icon" aria-hidden="true">
            <i class="pi" [class.pi-upload]="!fileError" [class.pi-times-circle]="!!fileError"></i>
          </span>
          <span class="bi-drop__title">
            {{ (fileError ? "BUSINESS_IMPORT.UPLOAD.DROP_AGAIN" : "BUSINESS_IMPORT.UPLOAD.DROP") | translate }}
          </span>
          <span class="bi__muted bi-drop__or">{{ "BUSINESS_IMPORT.UPLOAD.OR" | translate }}</span>
          <button type="button" class="btn btn-primary" (click)="openFilePicker()">
            <i class="pi pi-file-excel" aria-hidden="true"></i>
            <span>{{ (fileError ? "BUSINESS_IMPORT.UPLOAD.CHOOSE_ANOTHER" : "BUSINESS_IMPORT.UPLOAD.CHOOSE") | translate }}</span>
          </button>
          <span class="bi__small">{{ "BUSINESS_IMPORT.UPLOAD.DROP_HINT" | translate }}</span>
        </div>
      </ng-template>

      <p *ngIf="selectedFile" class="bi__muted">{{ "BUSINESS_IMPORT.UPLOAD.HELP" | translate }}</p>
    </div>

    <aside class="bi-upload__aside">
      <details class="bi-req" open>
        <summary class="bi-req__summary">
          <span class="bi__h3">{{ "BUSINESS_IMPORT.REQUIREMENTS.TITLE" | translate }}</span>
          <span class="bi__small bi-req__short">{{ "BUSINESS_IMPORT.REQUIREMENTS.SUMMARY" | translate }}</span>
        </summary>
        <ul class="bi-req__list">
          <li [class.bi-req--ok]="!!selectedFile" [class.bi-req--error]="!!fileError && !!rejectedFileName && !rejectedFileName.toLowerCase().endsWith('.xlsx')">
            <i
              class="pi"
              aria-hidden="true"
              [class.pi-check-circle]="!!selectedFile"
              [class.pi-times-circle]="!selectedFile && !!rejectedFileName && !rejectedFileName.toLowerCase().endsWith('.xlsx')"
              [class.pi-info-circle]="!selectedFile && !(rejectedFileName && !rejectedFileName.toLowerCase().endsWith('.xlsx'))"
            ></i>
            <span>
              <strong>{{ "BUSINESS_IMPORT.REQUIREMENTS.FORMAT" | translate }}</strong>
              <span>{{ "BUSINESS_IMPORT.REQUIREMENTS.FORMAT_HINT" | translate }}</span>
            </span>
          </li>
          <li [class.bi-req--ok]="!!selectedFile">
            <i class="pi" aria-hidden="true" [class.pi-check-circle]="!!selectedFile" [class.pi-info-circle]="!selectedFile"></i>
            <span>
              <strong>{{ "BUSINESS_IMPORT.REQUIREMENTS.SIZE" | translate }}</strong>
              <span>{{ "BUSINESS_IMPORT.REQUIREMENTS.SIZE_HINT" | translate }}</span>
            </span>
          </li>
          <li>
            <i class="pi pi-info-circle" aria-hidden="true"></i>
            <span>
              <strong>{{ "BUSINESS_IMPORT.REQUIREMENTS.COLUMNS" | translate }}</strong>
              <span>{{ "BUSINESS_IMPORT.REQUIREMENTS.COLUMNS_HINT" | translate }}</span>
            </span>
          </li>
        </ul>
        <span class="bi__label">{{ "BUSINESS_IMPORT.REQUIREMENTS.REQUIRED" | translate }}</span>
        <ul class="bi-chips">
          <li *ngFor="let field of requiredFields">{{ fieldLabels[field] | translate }}</li>
        </ul>
        <span class="bi__label">{{ "BUSINESS_IMPORT.REQUIREMENTS.OPTIONAL" | translate }}</span>
        <ul class="bi-chips bi-chips--optional">
          <li>{{ "BUSINESS_IMPORT.HEADERS.COMPANY_NUMBER" | translate }}</li>
          <li>{{ "BUSINESS_IMPORT.HEADERS.TELEPHONE" | translate }}</li>
          <li>{{ "BUSINESS_IMPORT.HEADERS.ADDRESS" | translate }}</li>
          <li>{{ "BUSINESS_IMPORT.HEADERS.ZIPCODE" | translate }}</li>
          <li>{{ "BUSINESS_IMPORT.HEADERS.ABOUT_BUSINESS" | translate }}</li>
          <li *ngFor="let field of optionalDefaultFields">{{ fieldLabels[field] | translate }}</li>
        </ul>
        <p class="bi__small">{{ "BUSINESS_IMPORT.REQUIREMENTS.DEFAULTS_HINT" | translate }}</p>
      </details>
    </aside>
  </section>

  <!-- ================= Preview ================= -->
  <ng-container *ngIf="!loading && step === 'preview'">
    <div class="bi-banner" [class.bi-banner--info]="errorRowCount > 0" [class.bi-banner--ok]="errorRowCount === 0" role="status">
      <i class="pi" [class.pi-info-circle]="errorRowCount > 0" [class.pi-check-circle]="errorRowCount === 0" aria-hidden="true"></i>
      <div>
        <h2 #stepHeading tabindex="-1" class="bi__banner-title">
          {{
            (errorRowCount > 0 ? "BUSINESS_IMPORT.PREVIEW.BANNER_ATTENTION" : "BUSINESS_IMPORT.PREVIEW.BANNER_READY")
              | translate: { count: errorRowCount }
          }}
        </h2>
        <span>
          {{ (errorRowCount > 0 ? "BUSINESS_IMPORT.PREVIEW.BANNER_ATTENTION_BODY" : "BUSINESS_IMPORT.PREVIEW.BANNER_READY_BODY") | translate }}
        </span>
      </div>
    </div>

    <div class="bi-stats bi-stats--5">
      <div class="bi-stat bi-stat--muted">
        <span class="bi-stat__label"><i class="pi pi-table" aria-hidden="true"></i>{{ "BUSINESS_IMPORT.COUNTS.TOTAL" | translate }}</span>
        <span class="bi-stat__value kuba-mono">{{ totalCount }}</span>
      </div>
      <div class="bi-stat bi-stat--ok">
        <span class="bi-stat__label"><i class="pi pi-check-circle" aria-hidden="true"></i>{{ "BUSINESS_IMPORT.COUNTS.VALID" | translate }}</span>
        <span class="bi-stat__value kuba-mono">{{ validRowCount }}</span>
      </div>
      <div class="bi-stat bi-stat--error">
        <span class="bi-stat__label"><i class="pi pi-times-circle" aria-hidden="true"></i>{{ "BUSINESS_IMPORT.COUNTS.ERRORS" | translate }}</span>
        <span class="bi-stat__value kuba-mono">{{ errorRowCount }}</span>
        <span class="bi-stat__note">{{ "BUSINESS_IMPORT.COUNTS.ERRORS_NOTE" | translate }}</span>
      </div>
      <div class="bi-stat bi-stat--warn">
        <span class="bi-stat__label"><i class="pi pi-exclamation-triangle" aria-hidden="true"></i>{{ "BUSINESS_IMPORT.COUNTS.WARNINGS" | translate }}</span>
        <span class="bi-stat__value kuba-mono">{{ warningRowCount }}</span>
      </div>
      <div class="bi-stat bi-stat--primary bi-stat--wide-only">
        <span class="bi-stat__label"><i class="pi pi-building" aria-hidden="true"></i>{{ "BUSINESS_IMPORT.COUNTS.EXPECTED_CREATED" | translate }}</span>
        <span class="bi-stat__value kuba-mono">{{ validRowCount }}</span>
      </div>
    </div>

    <div *ngIf="preview && preview.DuplicateOrganizationNumbers.length > 0" class="bi-banner bi-banner--warn" role="note">
      <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
      <div>
        <strong>{{ "BUSINESS_IMPORT.DUPLICATES" | translate }}</strong>
        <span><span class="kuba-mono">{{ preview.DuplicateOrganizationNumbers.join(", ") }}</span>. {{ "BUSINESS_IMPORT.PREVIEW.DUPLICATES_BODY" | translate }}</span>
      </div>
    </div>

    <section class="bi-card bi-review" [attr.aria-label]="'BUSINESS_IMPORT.PREVIEW.ROWS_LABEL' | translate">
      @let reviewRows = filteredRows;
      <div class="bi-toolbar">
        <div class="bi-tabs" role="group" [attr.aria-label]="'BUSINESS_IMPORT.PREVIEW.FILTER_LABEL' | translate">
          <button
            *ngFor="
              let tab of [
                { key: 'attention', label: 'FILTER_ATTENTION', count: attentionCount },
                { key: 'all', label: 'FILTER_ALL', count: totalCount },
                { key: 'errors', label: 'FILTER_ERRORS', count: errorRowCount },
                { key: 'warnings', label: 'FILTER_WARNINGS', count: warningRowCount },
                { key: 'edited', label: 'FILTER_EDITED', count: editedRowCount }
              ]
            "
            type="button"
            class="bi-tab"
            [attr.aria-pressed]="previewFilter === tab.key"
            (click)="setPreviewFilter($any(tab.key)); previewPages.changePage(0)"
          >
            {{ "BUSINESS_IMPORT.PREVIEW." + tab.label | translate }}
            <span class="bi-tab__count kuba-mono">{{ tab.count }}</span>
          </button>
        </div>
        <div class="bi-toolbar__right">
          <label class="bi-search">
            <span class="bi__sr-only">{{ "BUSINESS_IMPORT.PREVIEW.SEARCH" | translate }}</span>
            <i class="pi pi-search" aria-hidden="true"></i>
            <input
              pInputText
              type="search"
              [(ngModel)]="search"
              (ngModelChange)="previewPages.changePage(0)"
              [placeholder]="'BUSINESS_IMPORT.PREVIEW.SEARCH' | translate"
            />
          </label>
          <button
            type="button"
            class="btn btn-default bi-optional-toggle"
            [attr.aria-pressed]="showOptionalColumns"
            (click)="showOptionalColumns = !showOptionalColumns"
          >
            <i class="pi pi-sliders-h" aria-hidden="true"></i>
            <span>{{ "BUSINESS_IMPORT.PREVIEW.OPTIONAL_FIELDS" | translate }}</span>
          </button>
        </div>
      </div>

      <p-paginator
        #previewPages
        class="bi-pagination"
        [rows]="25"
        [totalRecords]="reviewRows.length"
        [showPageLinks]="false"
        [showCurrentPageReport]="true"
        [currentPageReportTemplate]="'SUMMARY_TOTALRECORDS' | translate"
      />
      <p *ngIf="rows.length === 0" class="bi-empty">{{ "BUSINESS_IMPORT.PREVIEW.NO_ROWS" | translate }}</p>
      <p *ngIf="rows.length > 0 && reviewRows.length === 0" class="bi-empty">{{ "BUSINESS_IMPORT.PREVIEW.NO_MATCHES" | translate }}</p>

      <!-- One element per row: a table row on wide screens, a card on narrow screens (CSS). -->
      <div *ngIf="reviewRows.length > 0" class="bi-rows" [class.bi-rows--optional]="showOptionalColumns">
        <div class="bi-rows__head" aria-hidden="true">
          <span>{{ "BUSINESS_IMPORT.HEADERS.ROW" | translate }}</span>
          <span>{{ "BUSINESS_IMPORT.HEADERS.STATUS" | translate }}</span>
          <span *ngFor="let field of requiredFields">{{ fieldLabels[field] | translate }} *</span>
          <span>{{ "BUSINESS_IMPORT.HEADERS.ACTIONS" | translate }}</span>
        </div>
        <article
          *ngFor="let row of reviewRows | slice: previewPages.first : previewPages.first + previewPages.rows; trackBy: trackRow"
          class="bi-row bi-row--{{ rowState(row) }}"
          [attr.aria-label]="'BUSINESS_IMPORT.PREVIEW.ROW_N' | translate: { row: row.RowNumber }"
        >
          <div class="bi-row__num kuba-mono">
            <span class="bi-row__num-label">{{ "BUSINESS_IMPORT.PREVIEW.ROW_N" | translate: { row: row.RowNumber } }}</span>
            <span class="bi-row__num-value">{{ row.RowNumber }}</span>
          </div>
          <div class="bi-row__status">
            <ng-container [ngSwitch]="rowState(row)">
              <span *ngSwitchCase="'error'" class="kuba-status kuba-status--danger">
                <i class="pi pi-times-circle" aria-hidden="true"></i>
                {{ (errorCount(row) === 1 ? "BUSINESS_IMPORT.PREVIEW.ERROR_1" : "BUSINESS_IMPORT.PREVIEW.ERRORS_N") | translate: { count: errorCount(row) } }}
              </span>
              <span *ngSwitchCase="'edited'" class="kuba-status kuba-status--active">
                <i class="pi pi-pencil" aria-hidden="true"></i>{{ "BUSINESS_IMPORT.STATUS.EDITED" | translate }}
              </span>
              <span *ngSwitchCase="'warn'" class="kuba-status kuba-status--warning">
                <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>{{ "BUSINESS_IMPORT.STATUS.WARNING" | translate }}
              </span>
              <span *ngSwitchDefault class="kuba-status kuba-status--success">
                <i class="pi pi-check-circle" aria-hidden="true"></i>{{ "BUSINESS_IMPORT.STATUS.VALID" | translate }}
              </span>
            </ng-container>
            <span *ngIf="rowState(row) === 'edited'" class="bi__small">{{ "BUSINESS_IMPORT.PREVIEW.EDITED_NOTE" | translate }}</span>
            <span *ngFor="let error of generalErrors(row)" class="bi-msg bi-msg--error">
              <i class="pi pi-times-circle" aria-hidden="true"></i>{{ error }}
            </span>
            <span *ngFor="let warning of row.Warnings" class="bi-msg bi-msg--warn">
              <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>{{ warning }}
            </span>
          </div>
          <div *ngFor="let field of requiredFields" class="bi-row__field">
            <label class="bi-row__label" [attr.for]="'bi-' + row.RowNumber + '-' + field">{{ fieldLabels[field] | translate }} *</label>
            <input
              class="bi-input"
              [id]="'bi-' + row.RowNumber + '-' + field"
              [type]="field === 'Email' ? 'email' : 'text'"
              [class.kuba-mono]="field === 'OrganizationNumber'"
              [class.bi-input--error]="fieldErrors(row, field).length > 0"
              [attr.aria-invalid]="fieldErrors(row, field).length > 0 ? 'true' : null"
              [attr.aria-describedby]="fieldErrors(row, field).length > 0 ? 'bi-' + row.RowNumber + '-' + field + '-error' : null"
              [attr.aria-label]="('BUSINESS_IMPORT.PREVIEW.ROW_N' | translate: { row: row.RowNumber }) + ', ' + (fieldLabels[field] | translate)"
              [(ngModel)]="row.Values[field]"
              (ngModelChange)="onValueChange(row, field)"
            />
            <span
              *ngIf="fieldErrors(row, field).length > 0"
              class="bi-msg bi-msg--error"
              [id]="'bi-' + row.RowNumber + '-' + field + '-error'"
            >
              <i class="pi pi-times-circle" aria-hidden="true"></i>{{ fieldErrors(row, field).join(" ") }}
            </span>
          </div>
          <div class="bi-row__actions">
            <button
              type="button"
              class="bi-more"
              [attr.aria-expanded]="showOptionalColumns || expandedRows.has(row.RowNumber)"
              [attr.aria-controls]="'bi-optional-' + row.RowNumber"
              [attr.aria-label]="
                (showOptionalColumns || expandedRows.has(row.RowNumber)
                  ? 'BUSINESS_IMPORT.PREVIEW.HIDE_OPTIONAL'
                  : 'BUSINESS_IMPORT.PREVIEW.SHOW_OPTIONAL'
                ) | translate: { row: row.RowNumber }
              "
              [disabled]="showOptionalColumns"
              (click)="toggleRow(row)"
            >
              <i class="pi" [class.pi-chevron-down]="showOptionalColumns || expandedRows.has(row.RowNumber)" [class.pi-chevron-right]="!(showOptionalColumns || expandedRows.has(row.RowNumber))" aria-hidden="true"></i>
              <span>{{ (showOptionalColumns || expandedRows.has(row.RowNumber) ? "BUSINESS_IMPORT.PREVIEW.LESS" : "BUSINESS_IMPORT.PREVIEW.MORE") | translate }}</span>
            </button>
            <button
              type="button"
              class="kuba-icon-btn kuba-icon-btn--danger bi-remove"
              [title]="'BUSINESS_IMPORT.PREVIEW.REMOVE_ROW_N' | translate: { row: row.RowNumber }"
              [attr.aria-label]="'BUSINESS_IMPORT.PREVIEW.REMOVE_ROW_N' | translate: { row: row.RowNumber }"
              (click)="removeRow(row)"
            >
              <i class="pi pi-trash" aria-hidden="true"></i>
            </button>
          </div>
          <div
            *ngIf="showOptionalColumns || expandedRows.has(row.RowNumber)"
            class="bi-row__optional"
            [id]="'bi-optional-' + row.RowNumber"
          >
            <label>
              <span>{{ "BUSINESS_IMPORT.HEADERS.COMPANY_NUMBER" | translate }}</span>
              <input class="bi-input" type="text" [(ngModel)]="row.Values.CompanyNumber" />
            </label>
            <label>
              <span>{{ "BUSINESS_IMPORT.HEADERS.TELEPHONE" | translate }}</span>
              <input class="bi-input" type="tel" [(ngModel)]="row.Values.Telephone" />
            </label>
            <label>
              <span>{{ "BUSINESS_IMPORT.HEADERS.ADDRESS" | translate }}</span>
              <input class="bi-input" type="text" [(ngModel)]="row.Values.Address" />
            </label>
            <label>
              <span>{{ "BUSINESS_IMPORT.HEADERS.ZIPCODE" | translate }}</span>
              <input class="bi-input kuba-mono" type="text" [(ngModel)]="row.Values.Zipcode" />
            </label>
            <label class="bi-row__about">
              <span>{{ "BUSINESS_IMPORT.HEADERS.ABOUT_BUSINESS" | translate }}</span>
              <textarea class="bi-input" rows="2" [(ngModel)]="row.Values.AboutBusiness"></textarea>
            </label>
            <label *ngFor="let field of optionalDefaultFields">
              <span>{{ fieldLabels[field] | translate }}</span>
              <input
                class="bi-input"
                type="text"
                [id]="'bi-' + row.RowNumber + '-' + field"
                [class.bi-input--error]="fieldErrors(row, field).length > 0"
                [attr.aria-invalid]="fieldErrors(row, field).length > 0 ? 'true' : null"
                [attr.aria-describedby]="fieldErrors(row, field).length > 0 ? 'bi-' + row.RowNumber + '-' + field + '-error' : null"
                [(ngModel)]="row.Values[field]"
                (ngModelChange)="onValueChange(row, field)"
              />
              <span *ngIf="fieldErrors(row, field).length > 0" class="bi-msg bi-msg--error" [id]="'bi-' + row.RowNumber + '-' + field + '-error'">
                {{ fieldErrors(row, field).join(" ") }}
              </span>
            </label>
          </div>
        </article>
      </div>
    </section>
  </ng-container>

  <!-- ================= Complete ================= -->
  <section *ngIf="!loading && step === 'complete' && completion" class="bi-card bi-result">
    <div
      class="bi-outcome bi-outcome--{{ outcome }}"
      [attr.role]="outcome === 'none' ? 'alert' : 'status'"
    >
      <span class="bi-outcome__icon" aria-hidden="true">
        <i class="pi" [class.pi-check]="outcome === 'success'" [class.pi-exclamation-triangle]="outcome === 'partial'" [class.pi-times]="outcome === 'none'"></i>
      </span>
      <div>
        <span class="bi-outcome__eyebrow">{{ "BUSINESS_IMPORT.RESULT.EYEBROW_" + outcome.toUpperCase() | translate }}</span>
        <h2 #stepHeading tabindex="-1" class="bi__h2">
          {{
            "BUSINESS_IMPORT.RESULT." + outcome.toUpperCase() + "_TITLE"
              | translate: { count: completion.CreatedRows, created: completion.CreatedRows, total: completion.TotalRows }
          }}
        </h2>
        <p class="bi__muted">
          {{
            "BUSINESS_IMPORT.RESULT." + outcome.toUpperCase() + "_BODY"
              | translate: { failed: completion.FailedRows, skipped: completion.SkippedRows }
          }}
        </p>
      </div>
    </div>

    <div class="bi-stats bi-stats--3">
      <div class="bi-stat bi-stat--ok">
        <span class="bi-stat__label"><i class="pi pi-check-circle" aria-hidden="true"></i>{{ "BUSINESS_IMPORT.COUNTS.CREATED" | translate }}</span>
        <span class="bi-stat__value kuba-mono">{{ completion.CreatedRows }}</span>
      </div>
      <div class="bi-stat" [class.bi-stat--warn]="completion.SkippedRows > 0" [class.bi-stat--muted]="completion.SkippedRows === 0">
        <span class="bi-stat__label"><i class="pi pi-minus-circle" aria-hidden="true"></i>{{ "BUSINESS_IMPORT.COUNTS.SKIPPED" | translate }}</span>
        <span class="bi-stat__value kuba-mono">{{ completion.SkippedRows }}</span>
      </div>
      <div class="bi-stat" [class.bi-stat--error]="completion.FailedRows > 0" [class.bi-stat--muted]="completion.FailedRows === 0">
        <span class="bi-stat__label"><i class="pi pi-times-circle" aria-hidden="true"></i>{{ "BUSINESS_IMPORT.COUNTS.FAILED" | translate }}</span>
        <span class="bi-stat__value kuba-mono">{{ completion.FailedRows }}</span>
      </div>
    </div>

    <div class="bi-report">
      <div class="bi-report__info">
        <span class="bi-report__icon" aria-hidden="true"><i class="pi pi-file"></i></span>
        <div>
          <h3 class="bi__h3">{{ "BUSINESS_IMPORT.RESULT.REPORT_TITLE" | translate }}</h3>
          <p class="bi__muted">
            {{ (completion.JobId || completion.EvidenceReportBase64 ? "BUSINESS_IMPORT.RESULT.REPORT_BODY" : "BUSINESS_IMPORT.RESULT.REPORT_MISSING") | translate }}
          </p>
          <span *ngIf="completion.EvidenceReportFileName" class="bi__small kuba-mono bi__wrap">{{ completion.EvidenceReportFileName }}</span>
        </div>
      </div>
      <button
        type="button"
        class="btn btn-primary bi-report__button"
        [disabled]="reportLoading || (!completion.JobId && !completion.EvidenceReportBase64)"
        (click)="downloadEvidenceReport()"
      >
        <i class="pi pi-download" aria-hidden="true"></i>
        <span>{{ "BUSINESS_IMPORT.DOWNLOAD_REPORT" | translate }}</span>
      </button>
    </div>

    <div *ngIf="apiError" class="bi-banner bi-banner--warn" role="alert">
      <span>{{ apiError }}</span>
      <button type="button" class="btn btn-default" (click)="loadResultPage()">{{ "BUSINESS_IMPORT.JOB.RELOAD_RESULTS" | translate }}</button>
    </div>
    <button *ngIf="completion.JobId && completion.FailedRows > 0" type="button" class="btn btn-default" (click)="retryFailedRows()">
      {{ "BUSINESS_IMPORT.JOB.RETRY_FAILED" | translate }}
    </button>

    <div class="bi-result__body" [class.bi-result__body--split]="outcome !== 'success'">
      <div class="bi-result__rows">
        <button
          *ngIf="outcome === 'success'"
          type="button"
          class="bi-disclosure"
          [attr.aria-expanded]="showResultRows"
          aria-controls="bi-result-rows"
          (click)="showResultRows = !showResultRows"
        >
          <i class="pi" [class.pi-chevron-down]="showResultRows" [class.pi-chevron-right]="!showResultRows" aria-hidden="true"></i>
          {{ (showResultRows ? "BUSINESS_IMPORT.RESULT.HIDE_ROWS" : "BUSINESS_IMPORT.RESULT.SHOW_ROWS") | translate: { count: completion.TotalRows } }}
        </button>

        <div *ngIf="showResultRows" id="bi-result-rows" class="bi-result__table" [attr.aria-busy]="resultsLoading">
          @let filteredResults = resultRows;
          <div
            *ngIf="outcome !== 'success'"
            class="bi-tabs"
            role="group"
            [attr.aria-label]="'BUSINESS_IMPORT.PREVIEW.FILTER_LABEL' | translate"
          >
            <button
              *ngFor="
                let tab of [
                  { key: 'attention', label: 'FILTER_ATTENTION', count: resultAttentionCount },
                  { key: 'created', label: 'FILTER_CREATED', count: completion.CreatedRows },
                  { key: 'all', label: 'FILTER_ALL', count: completion.TotalRows }
                ]
              "
              type="button"
              class="bi-tab"
              [attr.aria-pressed]="resultFilter === tab.key"
              (click)="changeResultFilter($any(tab.key))"
            >
              {{ "BUSINESS_IMPORT.RESULT." + tab.label | translate }}
              <span class="bi-tab__count kuba-mono">{{ tab.count }}</span>
            </button>
          </div>
          <p-paginator
            #resultPages
            class="bi-pagination"
            [rows]="25"
            [first]="resultOffset"
            [totalRecords]="completion.JobId ? completion.MatchingRows : filteredResults.length"
            (onPageChange)="onResultPage($event)"
            [showPageLinks]="false"
            [showCurrentPageReport]="true"
            [currentPageReportTemplate]="'SUMMARY_TOTALRECORDS' | translate"
          />
          <div class="bi-table-wrap" tabindex="0" role="region" [attr.aria-label]="'BUSINESS_IMPORT.RESULT.ROWS_LABEL' | translate">
            <table class="bi-table">
              <thead>
                <tr>
                  <th scope="col">{{ "BUSINESS_IMPORT.HEADERS.ROW" | translate }}</th>
                  <th scope="col">{{ "BUSINESS_IMPORT.RESULT.OUTCOME" | translate }}</th>
                  <th scope="col">{{ "BUSINESS_IMPORT.HEADERS.ORGANIZATION_NUMBER" | translate }}</th>
                  <th scope="col">{{ "BUSINESS_IMPORT.HEADERS.COMPANY_NAME" | translate }}</th>
                  <th scope="col">{{ "BUSINESS_IMPORT.RESULT.REASON" | translate }}</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of filteredResults | slice: (completion.JobId ? 0 : resultPages.first) : (completion.JobId ? resultPages.rows : resultPages.first + resultPages.rows); trackBy: trackRow">
                  <td class="kuba-mono" [attr.data-label]="'BUSINESS_IMPORT.HEADERS.ROW' | translate">{{ row.RowNumber }}</td>
                  <td [attr.data-label]="'BUSINESS_IMPORT.RESULT.OUTCOME' | translate">
                    <span
                      class="kuba-status"
                      [class.kuba-status--success]="resultKind(row) === 'created'"
                      [class.kuba-status--neutral]="resultKind(row) === 'skipped'"
                      [class.kuba-status--danger]="resultKind(row) === 'failed'"
                    >
                      <i
                        class="pi"
                        aria-hidden="true"
                        [class.pi-check-circle]="resultKind(row) === 'created'"
                        [class.pi-minus-circle]="resultKind(row) === 'skipped'"
                        [class.pi-times-circle]="resultKind(row) === 'failed'"
                      ></i>
                      {{ statusKey(row.Status) | translate }}
                    </span>
                  </td>
                  <td class="kuba-mono" [attr.data-label]="'BUSINESS_IMPORT.HEADERS.ORGANIZATION_NUMBER' | translate">{{ row.Values.OrganizationNumber || "–" }}</td>
                  <td [attr.data-label]="'BUSINESS_IMPORT.HEADERS.COMPANY_NAME' | translate">{{ row.Values.CompanyName || "–" }}</td>
                  <td [attr.data-label]="'BUSINESS_IMPORT.RESULT.REASON' | translate">
                    <span *ngIf="resultKind(row) === 'created' && row.CreatedBusinessId" class="bi__muted">
                      {{ "BUSINESS_IMPORT.HEADERS.CREATED_BUSINESS_ID" | translate }}: <span class="kuba-mono">{{ row.CreatedBusinessId }}</span>
                    </span>
                    <span *ngFor="let error of row.Errors" class="bi-msg bi-msg--error"><i class="pi pi-times-circle" aria-hidden="true"></i>{{ error }}</span>
                    <span *ngFor="let warning of row.Warnings" class="bi-msg bi-msg--warn"><i class="pi pi-exclamation-triangle" aria-hidden="true"></i>{{ warning }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div *ngIf="outcome !== 'success'" class="bi-next">
        <h3 class="bi__h3">{{ "BUSINESS_IMPORT.RESULT.WHAT_NEXT" | translate }}</h3>
        <ol>
          <li>{{ "BUSINESS_IMPORT.RESULT.NEXT_1" | translate }}</li>
          <li>{{ "BUSINESS_IMPORT.RESULT.NEXT_2" | translate }}</li>
          <li>{{ "BUSINESS_IMPORT.RESULT.NEXT_3" | translate }}</li>
        </ol>
        <div class="bi-next__actions">
          <button type="button" class="btn btn-default" (click)="importAnother()">
            <i class="pi pi-upload" aria-hidden="true"></i>
            <span>{{ "BUSINESS_IMPORT.RESULT.IMPORT_CORRECTED" | translate }}</span>
          </button>
        </div>
      </div>
    </div>
  </section>

  <!-- ================= Request failure ================= -->
  <section *ngIf="!loading && step === 'failed'" class="bi-card bi-result">
    <div class="bi-outcome bi-outcome--none" role="alert">
      <span class="bi-outcome__icon" aria-hidden="true"><i class="pi pi-times"></i></span>
      <div>
        <span class="bi-outcome__eyebrow">{{ "BUSINESS_IMPORT.RESULT.EYEBROW_FAILED" | translate }}</span>
        <h2 #stepHeading tabindex="-1" class="bi__h2">{{ "BUSINESS_IMPORT.FAILURE.TITLE" | translate }}</h2>
        <p class="bi__muted">{{ "BUSINESS_IMPORT.FAILURE.BODY" | translate }}</p>
      </div>
    </div>

    <div class="bi-banner bi-banner--warn">
      <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
      <div>
        <strong>{{ "BUSINESS_IMPORT.FAILURE.DUPLICATE_RISK_TITLE" | translate }}</strong>
        <span>{{ "BUSINESS_IMPORT.ERRORS.CHECK_BEFORE_RETRY" | translate }}</span>
      </div>
    </div>

    <div class="bi-server-message">
      <span class="bi__label">{{ "BUSINESS_IMPORT.FAILURE.SERVER_MESSAGE" | translate }}</span>
      <blockquote>{{ apiError }}</blockquote>
    </div>

    <div class="bi-failure-actions">
      <h3 class="bi__h3">{{ "BUSINESS_IMPORT.FAILURE.WHAT_YOU_CAN_DO" | translate }}</h3>
      <div class="bi-failure-actions__buttons">
        <a class="btn btn-primary" [href]="businessListUrl" target="_blank" rel="noopener">
          <i class="pi pi-external-link" aria-hidden="true"></i>
          <span>{{ "BUSINESS_IMPORT.FAILURE.CHECK_LIST" | translate }}</span>
          <span class="bi__sr-only">({{ "BUSINESS_IMPORT.FAILURE.NEW_TAB" | translate }})</span>
        </a>
        <button *ngIf="!jobId" type="button" class="btn btn-default" (click)="backToReview()">
          <i class="pi pi-arrow-left" aria-hidden="true"></i>
          <span>{{ "BUSINESS_IMPORT.FAILURE.BACK_TO_REVIEW" | translate }}</span>
        </button>
        <button *ngIf="jobId" type="button" class="btn btn-primary" (click)="resumeImport(jobId)">
          {{ "BUSINESS_IMPORT.JOB.CHECK_PROGRESS" | translate }}
        </button>
        <button *ngIf="selectedFile && !jobProgress" type="button" class="btn btn-default" (click)="confirmImport()">
          <i class="pi pi-refresh" aria-hidden="true"></i>
          <span>{{ "BUSINESS_IMPORT.FAILURE.RETRY" | translate }}</span>
        </button>
      </div>
      <p class="bi__muted">{{ "BUSINESS_IMPORT.FAILURE.KEPT" | translate: { count: rows.length } }}</p>
    </div>

    <details class="bi-technical" open>
      <summary>{{ "BUSINESS_IMPORT.FAILURE.TECHNICAL" | translate }}</summary>
      <dl>
        <ng-container *ngIf="failureStatus !== null">
          <dt>{{ "BUSINESS_IMPORT.FAILURE.STATUS" | translate }}</dt>
          <dd class="kuba-mono">{{ failureStatus }}</dd>
        </ng-container>
        <ng-container *ngIf="failureReference">
          <dt>{{ "BUSINESS_IMPORT.FAILURE.REFERENCE" | translate }}</dt>
          <dd class="bi-technical__ref">
            <span class="kuba-mono bi__wrap">{{ failureReference }}</span>
            <button
              type="button"
              class="kuba-icon-btn"
              [attr.aria-label]="'BUSINESS_IMPORT.FAILURE.COPY_REFERENCE' | translate"
              [title]="'BUSINESS_IMPORT.FAILURE.COPY_REFERENCE' | translate"
              (click)="copyReference()"
            >
              <i class="pi" [class.pi-copy]="!referenceCopied" [class.pi-check]="referenceCopied" aria-hidden="true"></i>
            </button>
            <span *ngIf="referenceCopied" class="bi__small" role="status">{{ "BUSINESS_IMPORT.FAILURE.COPIED" | translate }}</span>
          </dd>
        </ng-container>
        <dt>{{ "BUSINESS_IMPORT.FAILURE.TIME" | translate }}</dt>
        <dd>{{ failureTime | date: "medium" }}</dd>
      </dl>
    </details>
  </section>

  <!-- Undo for row removal -->
  <div *ngIf="lastRemoved && !loading" class="bi-snackbar" role="status">
    <i class="pi pi-trash" aria-hidden="true"></i>
    <span>{{ "BUSINESS_IMPORT.PREVIEW.ROW_REMOVED" | translate: { row: lastRemoved.row.RowNumber } }}</span>
    <button type="button" (click)="undoRemove()">
      <i class="pi pi-undo" aria-hidden="true"></i>
      <span>{{ "BUSINESS_IMPORT.PREVIEW.UNDO" | translate }}</span>
    </button>
  </div>

  <!-- ================= Sticky action bar ================= -->
  <div class="bi-footer" role="region" [attr.aria-label]="'BUSINESS_IMPORT.ACTIONS_LABEL' | translate">
    <div class="bi-footer__info">
      <ng-container *ngIf="loading">
        <i class="pi pi-lock" aria-hidden="true"></i>
        <span>{{ "BUSINESS_IMPORT.PROCESSING.PAUSED" | translate }}</span>
      </ng-container>
      <ng-container *ngIf="!loading && step === 'upload'">
        <ng-container *ngIf="selectedFile">
          <i class="pi pi-lock" aria-hidden="true"></i>
          <span>{{ "BUSINESS_IMPORT.UPLOAD.NOTHING_CREATED" | translate }}</span>
        </ng-container>
        <ng-container *ngIf="!selectedFile && !fileError">
          <i class="pi pi-info-circle" aria-hidden="true"></i>
          <span>{{ "BUSINESS_IMPORT.UPLOAD.CHOOSE_TO_CONTINUE" | translate }}</span>
        </ng-container>
        <ng-container *ngIf="!selectedFile && fileError">
          <i class="pi pi-times-circle bi__error-text" aria-hidden="true"></i>
          <span class="bi__error-text">{{ "BUSINESS_IMPORT.UPLOAD.INVALID_TO_CONTINUE" | translate }}</span>
        </ng-container>
      </ng-container>
      <span *ngIf="!loading && step === 'preview'" class="bi-footer__summary">
        <strong>{{ "BUSINESS_IMPORT.PREVIEW.SUMMARY_WILL_CREATE" | translate: { count: validRowCount } }}</strong>
        <span>
          {{
            (errorRowCount > 0 ? "BUSINESS_IMPORT.PREVIEW.SUMMARY_WILL_FAIL" : "BUSINESS_IMPORT.PREVIEW.SUMMARY_ALL_VALID")
              | translate: { count: errorRowCount }
          }}
        </span>
      </span>
      <ng-container *ngIf="!loading && step === 'complete' && completion">
        <i class="pi" [class.pi-check-circle]="outcome === 'success'" [class.pi-exclamation-triangle]="outcome !== 'success'" aria-hidden="true"></i>
        <span>{{ "BUSINESS_IMPORT.RESULT.FINISHED_AT" | translate: { time: (completion.CompletedAtUtc | date: "HH:mm") } }}</span>
      </ng-container>
      <ng-container *ngIf="!loading && step === 'failed'">
        <i class="pi pi-times-circle bi__error-text" aria-hidden="true"></i>
        <span class="bi__error-text">{{ "BUSINESS_IMPORT.FAILURE.NO_RESULTS" | translate }}</span>
      </ng-container>
    </div>

    <div class="bi-footer__actions">
      <!-- A saved job continues when the admin leaves the page. -->
      <ng-container *ngIf="loading">
        <button *ngIf="!jobProgress?.JobId" type="button" class="btn btn-link" disabled>{{ "CANCEL" | translate }}</button>
        <button *ngIf="jobProgress?.JobId" type="button" class="btn btn-link" (click)="goToBusinesses()">{{ "BUSINESS_IMPORT.RESULT.GO_TO_BUSINESSES" | translate }}</button>
        <button type="button" class="btn btn-primary" disabled aria-disabled="true">
          <i class="pi pi-spinner pi-spin" aria-hidden="true"></i>
          <span>{{ (processing === "validating" ? "BUSINESS_IMPORT.PROCESSING.VALIDATING_BUTTON" : "BUSINESS_IMPORT.PROCESSING.IMPORTING_BUTTON") | translate }}</span>
        </button>
      </ng-container>

      <ng-container *ngIf="!loading && step === 'upload'">
        <a class="btn btn-link" routerLink="..">{{ "CANCEL" | translate }}</a>
        <button type="button" class="btn btn-primary" [disabled]="!selectedFile" (click)="previewImport()">
          <i class="pi pi-search" aria-hidden="true"></i>
          <span>{{ "BUSINESS_IMPORT.VALIDATE" | translate }}</span>
        </button>
      </ng-container>

      <ng-container *ngIf="!loading && step === 'preview'">
        <button type="button" class="btn btn-default" (click)="chooseAnotherFile()">
          <i class="pi pi-arrow-left" aria-hidden="true"></i>
          <span>{{ "BUSINESS_IMPORT.PREVIEW.CHOOSE_ANOTHER" | translate }}</span>
        </button>
        <button type="button" class="btn btn-primary" [disabled]="rows.length === 0" (click)="confirmImport()">
          <i class="pi pi-check" aria-hidden="true"></i>
          <span>{{ "BUSINESS_IMPORT.PREVIEW.IMPORT_N" | translate: { count: totalCount } }}</span>
        </button>
      </ng-container>

      <ng-container *ngIf="!loading && step === 'complete'">
        <button type="button" class="btn btn-link" (click)="importAnother()">{{ "BUSINESS_IMPORT.RESULT.IMPORT_ANOTHER" | translate }}</button>
        <button type="button" class="btn btn-default" (click)="goToBusinesses()">
          <i class="pi pi-building" aria-hidden="true"></i>
          <span>{{ "BUSINESS_IMPORT.RESULT.GO_TO_BUSINESSES" | translate }}</span>
        </button>
      </ng-container>

      <ng-container *ngIf="!loading && step === 'failed'">
        <button type="button" class="btn btn-link" (click)="importAnother()">{{ "BUSINESS_IMPORT.RESULT.IMPORT_ANOTHER" | translate }}</button>
        <button type="button" class="btn btn-link" (click)="goToBusinesses()">{{ "CLOSE" | translate }}</button>
      </ng-container>
    </div>
  </div>
</div>

<p-confirmDialog
  [header]="'CONFIRMATION' | translate"
  [acceptLabel]="'SWITCH_YES' | translate"
  [rejectLabel]="'SWITCH_NO' | translate"
></p-confirmDialog>
